/* eslint-disable import/no-unresolved */
import React, { Component } from "react";
import { shape, string, func, bool } from "prop-types";
import { connect } from "react-redux";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as transactionsActions } from "reducers/transactions";
import I18n from "pages/_components/I18n";
import FormattedDate from "pages/_components/FormattedDate";
import FormattedAmount from "pages/_components/FormattedAmount";
import * as configUtils from "util/config";
import * as i18nUtils from "util/i18n";
import { isMvp3PendingTransactionsEnabled, isTransactionPendingProcessing } from "util/transaction";
import Card from "pages/_components/Card/Card";
import Image from "pages/_components/Image";
import InfoTag from "pages/_components/InfoTag";
import ContextMenu from "pages/_components/ContextMenu";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { getLastUpdateUserName } from "util/transactionPreparedByDisplayName";
import { truncateWithEllipsis } from "util/string";
import { getEcomexBeneficiaryName } from "util/ecomex";
import { getTransferActivityName } from "util/transferInternalTitleLabels";

const EXTERNAL_ACTIVITY_IDS = new Set([
    "external.emit.echeq.activity",
    "external.emit.massive.echeq.activity",
    "send.external.transaction",
]);
const isExternalChannelTransaction = (idActivity, data, channel) =>
    EXTERNAL_ACTIVITY_IDS.has(idActivity) || data?.externalChannel === true || channel === "externo";

const computeHistoricTransactionDerived = (propsTransaction) => {
    const { transaction, transactionAmounts } = propsTransaction;
    const {
        creationDateTime,
        submitDateTime,
        activityName: rawActivityName,
        idTransactionStatus,
        draftTransaction,
        data,
        idActivity,
    } = transaction;
    const activityName = getTransferActivityName(idActivity, rawActivityName);
    const currency =
        activityName === "CREDIN" && draftTransaction && data.cbuList?.length
            ? data.cbuList[0].amount.currency
            : Object.keys(transactionAmounts)[0];
    const amount =
        activityName === "CREDIN" && draftTransaction
            ? data.cbuList?.reduce((acc, val) => val.amount.quantity + acc, 0)
            : transactionAmounts[currency];
    let showAmount = amount !== 0;
    let showEcomexBeneficiary = false;
    let ecomexBeneficiaryLabel = "";
    let ecomexBeneficiaryNameToShow = "";

    const showEcomexAdditionalData = configUtils.getBoolean("frotend.show.divisa.amount.Ecomex");

    if (idActivity === "ecomex.send.accept.transaction") {
        showAmount =
            configUtils.getBoolean("frontend.ecomex.check.nonDeduct.limits", false) && !showEcomexAdditionalData;
        ecomexBeneficiaryNameToShow = truncateWithEllipsis(
            getEcomexBeneficiaryName(data),
            configUtils.getInteger("frontend.show.beneficiary.ecomex.maxLength", 100),
        );
        ecomexBeneficiaryLabel = i18nUtils.get("ecomex.transaction.card.beneficiaryName");
        showEcomexBeneficiary =
            configUtils.getBoolean("frontend.show.beneficiary.ecomex", false) && !!ecomexBeneficiaryNameToShow;
    }

    let montoEcomex;
    if (idActivity === "ecomex.send.accept.transaction") {
        montoEcomex = new Intl.NumberFormat("es-AR", {
            style: "currency",
            currency: transaction?.data?.transactionData?.requestCurrency,
            currencyDisplay: "code",
        }).format(transaction?.data?.transactionData?.requestAmount);
    }

    const isMvp3 = isMvp3PendingTransactionsEnabled();
    const isPendingProcessing = isTransactionPendingProcessing(transaction);
    const statusForIcon = isMvp3 && isPendingProcessing ? "PROCESSING" : idTransactionStatus;
    const lastSignerDisplayName = getLastUpdateUserName(transaction) || null;

    return {
        creationDateTime,
        submitDateTime,
        activityName,
        idTransactionStatus,
        draftTransaction,
        data,
        idActivity,
        transaction,
        currency,
        amount,
        showAmount,
        showEcomexAdditionalData,
        showEcomexBeneficiary,
        ecomexBeneficiaryLabel,
        ecomexBeneficiaryNameToShow,
        isMvp3,
        isPendingProcessing,
        statusForIcon,
        lastSignerDisplayName,
        montoEcomex,
    };
};

class HistoricTransactionItemNew extends Component {
    static propTypes = {
        transaction: shape({
            cancelEnabled: string.isRequired,
            transaction: shape({
                creationDateTime: string,
                submitDateTime: string,
                activityName: string,
                idTransactionStatus: string,
            }),
            transactionAmounts: shape(),
        }).isRequired,
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
    };

    renderContexMenu = ({ idTransaction }) => {
        const { dispatch, isDesktop } = this.props;
        const items = [
            {
                label: "transactions.list.actions.delete",
                onClick: () => {
                    dispatch(transactionsActions.deleteDraftRequest(idTransaction));
                },
            },
        ];
        return (
            <ContextMenu
                items={items}
                isDesktop={isDesktop}
                imageDropdown="context-menu-black.svg"
                additionalClasses="btn btn-link btn-dropdown"
                dropBtnClass="p-0 m-0"
            />
        );
    };

    renderDesktopStatusColumn = (statusForIcon) => (
        <div className="d-flex flex-column min-width-3">
            <Image src={`images/transfer-${statusForIcon}.svg`} />
            <I18n
                componentProps={{ className: `transaction-status-text-${statusForIcon}` }}
                id={`transaction.status.${statusForIcon}`}
            />
        </div>
    );

    renderMobileStatusColumn = (statusForIcon, idTransactionStatus) => (
        <div className="d-flex align-items-center historic-transaction-item-new__icon-stack">
            {idTransactionStatus ? <Image src={`images/transfer-${statusForIcon}.svg`} /> : null}
            <I18n
                componentProps={{ className: `transaction-status-text-${statusForIcon}` }}
                id={`transaction.status.${statusForIcon}`}
            />
        </div>
    );

    /** Renders the Divisa/Importe i18n label used in mobile Ecomex rows. */
    renderEcomexCurrencyAmountLabels = () => (
        <span className="card__transaction-data-title-right historic-transaction-item-new__ecomex-currency-line">
            {i18nUtils.get("ecomex.transaction.data.requestCurrency")}/
            {i18nUtils.get("ecomex.transaction.data.requestAmount")}
        </span>
    );

    /** Renders the styled Ecomex request amount tag shared by desktop and mobile layouts. */
    renderEcomexAmountLabel = (montoEcomex) => (
        <div className="echeqs__cards-content card__transaction-ecomex-amount">
            <span className="echeqs__type">{montoEcomex}</span>
        </div>
    );

    /** Renders the Ecomex amount on mobile with normal weight, matching HistoricTransactionItem. */
    renderMobileEcomexAmount = (montoEcomex) => <p className="text-lead mt-0 mb-0 text-nowrap">{montoEcomex}</p>;

    /** Renders Divisa/Importe label and styled amount below the creation row on mobile. */
    renderMobileEcomexAmountRow = (d) => {
        if (!d.showEcomexAdditionalData || d.activityName !== "Comercio Exterior") {
            return null;
        }

        return (
            <div className="historic-transaction-item-new__meta-row">
                {this.renderEcomexCurrencyAmountLabels()}
                {this.renderMobileEcomexAmount(d.montoEcomex)}
            </div>
        );
    };

    renderMobileActivityHeading = (d) => {
        if (d.idActivity === "ecomex.send.accept.transaction") {
            if (d.showEcomexAdditionalData) {
                return (
                    <span className="card__transaction-ecomex-concept">
                        {d.activityName}|{" "}
                        {d.transaction?.data?.transactionData?.concept.trim().length > 35
                            ? `${d.transaction?.data?.transactionData?.concept.trim().substring(0, 32)}...`
                            : d.transaction?.data?.transactionData?.concept.trim()}
                    </span>
                );
            }

            return <span className="card__transaction-mobile-activity-title">{d.activityName}</span>;
        }
        if (d.idActivity === "transfer.multiple.send") {
            return (
                <I18n
                    id={getMultipleTransfersI18nKey("activities.transfer.multiple.send")}
                    componentProps={{ className: "card__transaction-mobile-activity-title" }}
                />
            );
        }
        if (d.activityName === "Descontar ECHEQs") {
            return (
                <I18n
                    id="echeqs.transaction.negotiate.label"
                    componentProps={{ className: "card__transaction-mobile-activity-title" }}
                />
            );
        }
        if (d.activityName === "Emitir Cesión") {
            return (
                <I18n
                    id="echeqs.transaction.cesion.label"
                    componentProps={{ className: "card__transaction-mobile-activity-title" }}
                />
            );
        }
        return <span className="card__transaction-mobile-activity-title">{d.activityName}</span>;
    };

    renderMobileTitleRow = (d) => (
        <div className="card__transaction-data-description historic-transaction-item-new__mobile-title-typography">
            <div className="historic-transaction-item-new__mobile-title-row">
                <div className="historic-transaction-item-new__mobile-title-text">
                    {this.renderMobileActivityHeading(d)}
                </div>
                <div className="historic-transaction-item-new__mobile-title-actions">
                    {isExternalChannelTransaction(d.idActivity, d.data, d.transaction?.channel) && (
                        <InfoTag
                            type="default"
                            message={i18nUtils.get("transactions.list.external.label", "Canal externo")}
                            tagClass="external-channel-tag"
                            capitalize
                        />
                    )}
                    {d.isMvp3 && d.isPendingProcessing && (
                        <InfoTag
                            type="info"
                            message={i18nUtils.get("transactions.pending.list.processing")}
                            tagClass="external-channel-tag"
                            capitalize
                        />
                    )}
                    {d.idTransactionStatus === "DRAFT" && this.renderContexMenu(d.transaction)}
                </div>
            </div>
        </div>
    );

    renderMobileMvp3DatesBlock = (d) => (
        <div className="historic-transaction-item-new__mvp3-dates">
            <div className="historic-transaction-item-new__mvp3-upper">
                <div className="historic-transaction-item-new__mvp3-labels">
                    <span className="card__transaction-data-title-left card__transaction-data-title-left--no-trailing-colon">
                        {i18nUtils.get("transactions.history.lastUpdate")}
                    </span>
                    {d.lastSignerDisplayName ? (
                        <span className="card__transaction-data-title-left">
                            {`${i18nUtils.get("transactions.history.byUser")} ${d.lastSignerDisplayName}`}
                        </span>
                    ) : null}
                </div>
                <div className="historic-transaction-item-new__mvp3-submit-date-wrap">
                    <FormattedDate className="card__transaction-data-time-left" showTime date={d.submitDateTime} />
                </div>
            </div>
            <div className="historic-transaction-item-new__mvp3-creation-row">
                <I18n
                    componentProps={{ className: "card__transaction-data-title-right" }}
                    id="transactions.list.header.creationDate"
                />
                <FormattedDate className="card__transaction-data-time-rigth" showTime date={d.creationDateTime} />
            </div>
            {this.renderMobileEcomexAmountRow(d)}
        </div>
    );

    renderMobileClassicMetaBlock = (d) => (
        <div className="historic-transaction-item-new__classic-meta">
            {d.showEcomexAdditionalData && d.idActivity === "ecomex.send.accept.transaction" ? (
                <div className="historic-transaction-item-new__meta-row">
                    <I18n
                        componentProps={{ className: "card__transaction-data-title-left mt-4" }}
                        id="transactions.list.item.PENDING"
                    />
                    <FormattedDate className="card__transaction-data-time-left" showTime date={d.submitDateTime} />
                </div>
            ) : null}
            {d.showEcomexAdditionalData === false && d.idActivity === "ecomex.send.accept.transaction" ? (
                <div className="historic-transaction-item-new__meta-row">
                    <I18n
                        componentProps={{ className: "card__transaction-data-title-left mt-2" }}
                        id="transactions.list.item.PENDING"
                    />
                    <FormattedDate className="card__transaction-data-time-left" showTime date={d.submitDateTime} />
                </div>
            ) : null}
            {d.idActivity !== "ecomex.send.accept.transaction" ? null : (
                <div className="historic-transaction-item-new__meta-row">
                    <I18n
                        componentProps={{ className: "card__transaction-data-title-left mt-2" }}
                        id="transactions.list.item.PENDING"
                    />
                    <FormattedDate className="card__transaction-data-time-left" showTime date={d.submitDateTime} />
                </div>
            )}
            <div className="historic-transaction-item-new__meta-row">
                <I18n
                    componentProps={{ className: "card__transaction-data-title-right" }}
                    id="transactions.list.header.creationDate"
                />
                <FormattedDate className="card__transaction-data-time-rigth" showTime date={d.creationDateTime} />
            </div>
            {this.renderMobileEcomexAmountRow(d)}
        </div>
    );

    renderDesktopTitleRow = (isMvp3, submitDateTime, creationDateTime, lastSignerDisplayName) => (
        <div className={`card__transaction-data-title${isMvp3 ? " card__transaction-data-title--mvp3" : ""}`}>
            <div className="d-flex">
                {isMvp3 ? (
                    <>
                        <I18n
                            componentProps={{ className: "card__transaction-data-title-left" }}
                            id="transactions.history.lastUpdate"
                        />
                        <FormattedDate className="card__transaction-data-time-left" showTime date={submitDateTime} />
                        {lastSignerDisplayName && (
                            <>
                                <span className="card__transaction-data-title-left">
                                    {i18nUtils.get("transactions.history.byUser")}
                                </span>
                                <span className="card__transaction-data-user">{lastSignerDisplayName}</span>
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <I18n
                            componentProps={{ className: "card__transaction-data-title-left" }}
                            id="transactions.list.item.PENDING"
                        />
                        <FormattedDate className="card__transaction-data-time-left" showTime date={submitDateTime} />
                    </>
                )}
                <I18n
                    componentProps={{ className: "card__transaction-data-title-right" }}
                    id="transactions.list.header.creationDate"
                />
                <FormattedDate className="card__transaction-data-time-rigth" showTime date={creationDateTime} />
            </div>
        </div>
    );

    renderDesktopDescription = (d) => (
        <div className="card__transaction-data-description">
            {d.idActivity === "ecomex.send.accept.transaction" && d.showEcomexAdditionalData ? (
                <div className="d-flex flex-column">
                    <div className="card__transaction-ecomex-row">
                        <span className="card__transaction-ecomex-concept">
                            {d.activityName} | {d.transaction?.data?.transactionData?.concept || ""}
                        </span>
                        {this.renderEcomexAmountLabel(d.montoEcomex)}
                    </div>
                    {d.showEcomexBeneficiary && (
                        <span>
                            <strong>
                                {d.ecomexBeneficiaryLabel} {d.ecomexBeneficiaryNameToShow}
                            </strong>
                        </span>
                    )}
                </div>
            ) : null}
            {d.idActivity === "ecomex.send.accept.transaction" && !d.showEcomexAdditionalData ? (
                <div className="d-flex flex-column">
                    <span>{d.activityName}</span>
                    {d.showEcomexBeneficiary && (
                        <span>
                            <strong>
                                {d.ecomexBeneficiaryLabel} {d.ecomexBeneficiaryNameToShow}
                            </strong>
                        </span>
                    )}
                </div>
            ) : null}
            {d.idActivity === "transfer.multiple.send" && (
                <I18n id={getMultipleTransfersI18nKey("activities.transfer.multiple.send")} />
            )}
            {d.activityName === "Descontar ECHEQs" ? <I18n id="echeqs.transaction.negotiate.label" /> : null}
            {d.activityName === "Emitir Cesión" && d.activityName !== "Descontar ECHEQs" ? (
                <I18n id="echeqs.transaction.cesion.label" />
            ) : null}
            {d.activityName !== "Descontar ECHEQs" &&
            d.activityName !== "Emitir Cesión" &&
            d.activityName !== "Comercio Exterior" &&
            d.idActivity !== "transfer.multiple.send" ? (
                <span>{d.activityName}</span>
            ) : null}
            {isExternalChannelTransaction(d.idActivity, d.data, d.transaction?.channel) && (
                <InfoTag
                    type="default"
                    message={i18nUtils.get("transactions.list.external.label", "Canal externo")}
                    tagClass="external-channel-tag"
                    capitalize
                />
            )}
            {d.isMvp3 && d.isPendingProcessing && (
                <InfoTag
                    type="default"
                    message={i18nUtils.get("transactions.pending.list.processing")}
                    tagClass="external-channel-tag"
                    capitalize
                />
            )}
        </div>
    );

    renderRightAmount = (showAmount, idTransactionStatus, currency, amount) => (
        <div className="card__component-item-currency d-flex align-items-center">
            {showAmount && (
                <FormattedAmount
                    {...(idTransactionStatus === "DRAFT" && {
                        className: "data-amount-gray-color",
                    })}
                    currency={currency}
                    quantity={amount}
                />
            )}
        </div>
    );

    renderRightAmountMobile = (showAmount, currency, amount) => (
        <div className="card__component-item-currency">
            {showAmount && <FormattedAmount currency={currency} quantity={amount} />}
        </div>
    );

    renderCardDesktop = (d) => (
        <Card
            className="card__transaction"
            stackContentBelow={false}
            iconWithText={this.renderDesktopStatusColumn(d.statusForIcon)}
            rightArrow={d.idTransactionStatus !== "DRAFT"}
            rightContextMenu={d.idTransactionStatus === "DRAFT" && this.renderContexMenu(d.transaction)}
            title={this.renderDesktopTitleRow(d.isMvp3, d.submitDateTime, d.creationDateTime, d.lastSignerDisplayName)}
            content={this.renderDesktopDescription(d)}
            rightContent={this.renderRightAmount(d.showAmount, d.idTransactionStatus, d.currency, d.amount)}
        />
    );

    renderCardMobile = (d) => (
        <Card
            className="card__transaction card__transaction--stack-body"
            stackContentBelow
            rightArrow={d.idTransactionStatus !== "DRAFT"}
            iconWithText={this.renderMobileStatusColumn(d.statusForIcon, d.idTransactionStatus)}
            title={this.renderMobileTitleRow(d)}
            content={
                <div className="historic-transaction-item-new__stack-body">
                    <div className="card__transaction-data-description historic-transaction-item-new__stack-dates-typography">
                        {d.isMvp3 ? this.renderMobileMvp3DatesBlock(d) : this.renderMobileClassicMetaBlock(d)}
                    </div>
                </div>
            }
            mobileBottom={
                d.showEcomexBeneficiary ? (
                    <span className="card__transaction-beneficiary-mobile">
                        {d.ecomexBeneficiaryLabel.replace(/:\s*$/, "")}: {d.ecomexBeneficiaryNameToShow}
                    </span>
                ) : null
            }
            rightContent={this.renderRightAmountMobile(d.showAmount, d.currency, d.amount)}
        />
    );

    render() {
        const { transaction: propsTransaction, isDesktop } = this.props;
        const derived = computeHistoricTransactionDerived(propsTransaction);
        return <>{isDesktop ? this.renderCardDesktop(derived) : this.renderCardMobile(derived)}</>;
    }
}

const mapStateToProps = (state) => ({
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
});

export default connect(mapStateToProps)(HistoricTransactionItemNew);
